import { defineStore } from 'pinia'
import { useAuthStore } from '~/stores/useAuthStore'

/**
 * In-memory cache for schedule booking responses, keyed by query signature
 * (api method + dates + employee IDs + accountId). Survives navigation
 * between routes (so leaving /schedule and coming back paints from cache),
 * dies on full page reload (which is the correct boundary — schedule data
 * gets stale fast and Centrifuge events that fire while the tab is closed
 * are lost, so we re-fetch on cold load to be safe).
 *
 * Deliberately not persisted to Dexie. See .docs/performance.md — schedule
 * reservations are operationally sensitive (booking conflicts, double-bookings)
 * in a way customer/employee lists are not. The stale-then-refresh pattern is
 * fine within a single session where the WS keeps things current; it is risky
 * across reloads where the cache could be hours old.
 *
 * The cache wipes itself on account switch via a watcher on `authStore.activeGuid`.
 * The accountId is also part of every cache key as a defence-in-depth correctness
 * backstop — even if a stale entry survived, it could never be served to the
 * wrong account.
 */
const CACHE_LIMIT = 20

export const useScheduleCacheStore = defineStore('scheduleCache', () => {
  const authStore = useAuthStore()

  // Plain Map (not reactive) — consumers don't need reactivity here, just
  // O(1) identity-keyed lookup. Reactive wrapping would add overhead with
  // no benefit and could cause stale-deps warnings during sync writes.
  const entries = new Map<string, any>()

  function get(key: string): any | undefined {
    return entries.get(key)
  }

  function set(key: string, value: any) {
    // Re-insert to bump recency (LRU via Map insertion order).
    if (entries.has(key)) entries.delete(key)
    entries.set(key, value)
    while (entries.size > CACHE_LIMIT) {
      const oldest = entries.keys().next().value
      if (oldest !== undefined) entries.delete(oldest)
    }
  }

  function clear() {
    entries.clear()
  }

  // Wipe on account switch — keeps memory honest even if `activeGuid` flips
  // without unmounting the schedule controller (e.g. switcher swap that
  // redirects back to the same schedule route).
  watch(() => authStore.activeGuid, (next, prev) => {
    if (next !== prev) clear()
  })

  return { get, set, clear }
})
